import {dataTestId} from '@hconnect/uikit'
import {Box, Button, Stack, Typography} from '@mui/material'
import {AxiosError} from 'axios'
import React from 'react'
import {ErrorBoundary, ErrorBoundaryPropsWithRender} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router'

import {FEATURE_NAMESPACE} from '../consts'

export type OperatorViewErrorBoundaryProps = {
  title?: string
  children?: React.ReactNode
  fallbackTestId?: string
  onRetry?: () => void
}

const isAxiosError = (error: Error): error is AxiosError<{detail: string}> =>
  (error as AxiosError).name === 'AxiosError'

export const OperatorViewErrorBoundary = ({
  title,
  children,
  fallbackTestId,
  onRetry
}: OperatorViewErrorBoundaryProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {pathname} = useLocation()

  const fallbackRender: ErrorBoundaryPropsWithRender['fallbackRender'] = ({
    error,
    resetErrorBoundary
  }) => {
    return (
      <Box sx={{display: 'flex', p: 3}} {...dataTestId(fallbackTestId ?? 'error_fallback')}>
        <Stack spacing={2} sx={{alignItems: 'center', width: 1}}>
          <Typography variant="h3">{title ?? t('error.apology')}</Typography>
          <Typography variant="body1">{`${error.name}: ${error.message}`}</Typography>
          {isAxiosError(error) && error.response?.data?.detail && (
            <Typography variant="caption">{error.response.data.detail}</Typography>
          )}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => {
                onRetry?.()
                resetErrorBoundary()
              }}
            >
              {t('common.tryAgain')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    )
  }

  return (
    <ErrorBoundary fallbackRender={fallbackRender} resetKeys={[pathname]}>
      {children}
    </ErrorBoundary>
  )
}
